/**
 * Summary chips for tabbed Skill Badges + Certifications headers (My Team, My Insights, DRI Insights).
 * Active / Expiring / Expired pills sit inside one dotted-outline group; each pill keeps a solid border.
 */
import React from 'react';
import { Box, Chip, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { SEARCE_COLORS } from '../../theme';
import { formatNumber } from '../../utils/FormatUtils';
import type { CertificationStatusCounts } from '../../utils/skillsCertsTabMetrics';

const pillBlue: SxProps<Theme> = {
  backgroundColor: alpha(SEARCE_COLORS.primary, 0.14),
  color: SEARCE_COLORS.primaryDark,
  fontWeight: 700,
  border: `1px solid ${alpha(SEARCE_COLORS.primaryDark, 0.38)}`,
  borderRadius: 999,
  height: 30,
  '& .MuiChip-label': { px: 2, fontSize: '0.8125rem' },
};

const pillGreen: SxProps<Theme> = {
  backgroundColor: alpha(SEARCE_COLORS.success, 0.22),
  color: SEARCE_COLORS.success_700,
  fontWeight: 700,
  border: 'none',
  borderRadius: 999,
  height: 30,
  '& .MuiChip-label': { px: 2, fontSize: '0.8125rem' },
};

const pillRed: SxProps<Theme> = {
  backgroundColor: alpha(SEARCE_COLORS.error, 0.14),
  color: SEARCE_COLORS.errorDark,
  fontWeight: 700,
  border: `1px solid ${alpha(SEARCE_COLORS.errorDark, 0.42)}`,
  borderRadius: 999,
  height: 30,
  '& .MuiChip-label': { px: 2, fontSize: '0.8125rem' },
};

const pillAmber: SxProps<Theme> = {
  backgroundColor: alpha(SEARCE_COLORS.warning, 0.2),
  color: SEARCE_COLORS.warningDark,
  fontWeight: 700,
  border: `1px solid ${alpha(SEARCE_COLORS.warningDark, 0.42)}`,
  borderRadius: 999,
  height: 30,
  '& .MuiChip-label': { px: 2, fontSize: '0.8125rem' },
};

export interface SkillsCertsTabSummaryChipsProps {
  skillCount: number;
  certMetrics: CertificationStatusCounts;
  showSkillPills: boolean;
  showCertPills: boolean;
}

export function SkillsCertsTabSummaryChips({
  skillCount,
  certMetrics,
  showSkillPills,
  showCertPills,
}: SkillsCertsTabSummaryChipsProps): React.ReactElement | null {
  if (!showSkillPills && !showCertPills) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        pb: { xs: 1, sm: 0.75 },
        pl: { xs: 0.5, sm: 0 },
        flexShrink: 0,
        alignSelf: { xs: 'stretch', sm: 'center' },
      }}
    >
      {showSkillPills && (
        <Chip variant="filled" label={`Skill badges: ${formatNumber(skillCount)}`} sx={pillGreen} />
      )}
      {showCertPills && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
            px: 1,
            borderRadius: 2,
            border: `2px dotted ${alpha(SEARCE_COLORS.neutral_500, 0.55)}`,
          }}
          aria-label="Certification status summary"
        >
          <Chip variant="filled" label={`Active: ${formatNumber(certMetrics.active)}`} sx={pillBlue} />
          <Chip variant="filled" label={`Expiring: ${formatNumber(certMetrics.expiringSoon)}`} sx={pillAmber} />
          <Chip variant="filled" label={`Expired: ${formatNumber(certMetrics.expired)}`} sx={pillRed} />
        </Box>
      )}
    </Box>
  );
}
